import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../lib/api";
import { Input } from "../../components/ui/input";
import { PortalPage, EmptyState, Pill } from "../../components/portal/PortalPage";
import { fmtDate, JOB_STATUS } from "../../components/portal/portalUtils";
import { Briefcase, MagnifyingGlass, Camera, CheckCircle } from "@phosphor-icons/react";
import { TableSkeleton } from "../../components/Skeletons";
import { useTeamLabel } from "../../components/portal/PortalBrand";
import { ForClientChip, FamilyFilter, useFamilyFilter } from "../../components/portal/FamilyFilter";
import { SubJobChip } from "../../components/jobs/SubJobProgress";

export default function ClientJobsPage() {
  const teamLabel = useTeamLabel();
  const [view, setView] = useState("active");
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setJobs(null);
    API.get(`/client-hub/jobs?view=${view}`).then(({ data }) => setJobs(data.jobs || [])).catch(() => setJobs([]));
  }, [view]);

  const fam = useFamilyFilter();
  const filtered = (jobs || []).filter((j) => fam.matches(j) && (!q || `${j.reference} ${j.title} ${j.address} ${j.contractor || ""} ${j.for_client || ""}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <PortalPage title="Jobs" subtitle="Scheduled, in-progress and completed work across all your properties." testid="client-jobs">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between mb-4">
        <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
          {[["active", "Active"], ["closed", "Completed"], ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} className={`px-3 py-1 text-xs rounded ${view === k ? "bg-card shadow font-semibold" : "text-muted-foreground"}`} data-testid={`jobs-view-${k}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
        <FamilyFilter fam={fam} testid="jobs-client-filter" />
        <div className="relative sm:w-72">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs…" className="pl-8 h-8 text-xs" data-testid="jobs-search" />
        </div>
        </div>
      </div>
      <div className="border border-border rounded-md bg-card overflow-hidden">
        {jobs === null ? <div className="p-3"><TableSkeleton rows={5} cols={5} /></div> : filtered.length === 0 ? (
          <div className="p-6"><EmptyState icon={Briefcase} title="No jobs here yet" text="Jobs appear once a request has been approved and scheduled." /></div>
        ) : (
          <table className="w-full text-left" data-testid="jobs-table">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                <th className="px-4 py-2">Job</th>
                <th className="px-4 py-2 hidden md:table-cell">Property</th>
                <th className="px-4 py-2 hidden lg:table-cell">{teamLabel.heading}</th>
                <th className="px-4 py-2">Scheduled</th>
                <th className="px-4 py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((j) => (
                <tr key={j.id} className="row-hover cursor-pointer" onClick={() => navigate(`/portal/jobs/${j.id}`)} data-testid={`job-row-${j.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-[10px] font-mono text-muted-foreground flex items-center gap-1.5">{j.reference} <ForClientChip name={j.for_client} clientId={j.client_id} /><SubJobChip summary={j.sub_job_summary} testid={`client-job-subjobs-${j.id}`} /></p>
                    <p className="text-sm font-medium">{j.title}</p>
                    {j.photo_count > 0 && <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5"><Camera size={10} /> {j.photo_count} photo{j.photo_count > 1 ? "s" : ""}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell max-w-[260px] truncate">{j.address || "—"}</td>
                  <td className="px-4 py-3 text-xs hidden lg:table-cell" data-testid={`job-contractor-${j.id}`}>
                    {j.contractor ? j.contractor : j.contractor_assigned ? <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"><CheckCircle size={12} weight="fill" /> {teamLabel.brand ? teamLabel.assigned : "Assigned"}</span> : <span className="text-muted-foreground">TBC</span>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {j.scheduled_start ? <><p>{fmtDate(j.scheduled_start)}</p>{j.schedule?.start_time && <p className="text-[10px] text-muted-foreground">{j.schedule.start_time}{j.schedule.end_time ? ` — ${j.schedule.end_time}` : ""}</p>}</> : <span className="text-muted-foreground">TBC</span>}
                  </td>
                  <td className="px-4 py-3 text-right"><Pill map={JOB_STATUS} value={j.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </PortalPage>
  );
}
